import React, { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useUIStoreActions } from '@atlassian/conversation-assistant-store/controllers/ui-store';

import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';
import { useCurrentConversationId } from '../../controllers/chat-store/useCurrentConversationId';
import { useChatConversation } from '../../controllers/conversation/useChatConversation';

const StreamingStatusDispatcherInner = ({
	conversationId,
	conversation,
}: {
	conversationId: string;
	conversation: ReturnType<typeof useChatConversation>['conversation'];
}): null => {
	const { setStreamingStatus } = useUIStoreActions();
	const { updateConversation } = useChatStoreActions();
	// Track whether the conversation was previously in a streaming/loading state
	// so we only mark as read when a stream actually completed (not on initial page load)
	const wasStreamingRef = useRef(false);

	// Reset wasStreamingRef when the conversation changes so we don't carry over
	// streaming state from a previous conversation
	useEffect(() => {
		wasStreamingRef.current = false;
	}, [conversationId]);

	// ROVOFE-385 - Sync conversation load/streaming state with the UIStore for consumers to make decisions about their own implementations
	useEffect(() => {
		if (conversation) {
			const loading = [
				CHAT_STORE_STATUS.typingResponse,
				CHAT_STORE_STATUS.awaitingResponse,
			].includes(conversation?.status);
			if (loading) {
				setStreamingStatus(true);
			} else {
				setStreamingStatus(false);
			}
		}
		return () => {
			setStreamingStatus(false);
		};
	}, [conversation, setStreamingStatus]);

	useEffect(() => {
		if (!fg('rovo_chat_chat_history_refresh')) {
			return;
		}

		const isNowIdle = conversation?.status === CHAT_STORE_STATUS.idle;
		const isStreaming = [
			CHAT_STORE_STATUS.typingResponse,
			CHAT_STORE_STATUS.awaitingResponse,
		].includes(conversation?.status as CHAT_STORE_STATUS);

		// readStatus === false: optimistically set as unread (user was on a different conversation)
		// readStatus === undefined: not yet explicitly marked (user was on this conversation during streaming)
		// We gate on wasStreamingRef so we don't fire on initial page load when status starts idle
		// (where readStatus is undefined and the conversation was never in a streaming state for us).
		// However, if readStatus === false it means the server/another client explicitly marked this
		// conversation as unread while we were away, so we should mark it as read immediately once
		// it is idle — even if we never observed the streaming phase ourselves.
		const conversationNeedsMarkingRead = conversation?.readStatus !== true;
		const explicitlyUnread = conversation?.readStatus === false;
		const streamJustCompleted = wasStreamingRef.current && isNowIdle;
		// Mark as read if: stream just completed for us, OR we navigated to an already-idle
		// conversation that was explicitly marked unread (readStatus === false).
		const shouldMarkAsRead =
			(streamJustCompleted || (isNowIdle && explicitlyUnread)) && conversationNeedsMarkingRead;

		if (isStreaming) {
			wasStreamingRef.current = true;
		}

		if (shouldMarkAsRead && conversationId) {
			wasStreamingRef.current = false;
			void (async () => {
				await Promise.resolve(
					updateConversation({
						conversationId,
						data: { readStatus: true },
					}),
				);
			})();
		}
	}, [conversation?.status, conversation?.readStatus, conversationId, updateConversation]);

	return null;
};

export const StreamingStatusDispatcher = (): React.JSX.Element => {
	const conversationId = useCurrentConversationId();
	const { conversation } = useChatConversation({ conversationId });
	return (
		<StreamingStatusDispatcherInner conversationId={conversationId} conversation={conversation} />
	);
};
